import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../../services/authService';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [validToken, setValidToken] = useState(true);

  useEffect(() => {
    // Check if we have the required token parameters
    const accessToken = searchParams?.get('access_token');
    const type = searchParams?.get('type');
    
    if (!accessToken || type !== 'recovery') {
      setValidToken(false);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    // Validation
    if (password?.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const { error } = await authService?.updatePassword(password);
      
      if (error) {
        setError(error?.message);
      } else {
        setSuccess(true);
        // Redirect to signin after 3 seconds
        setTimeout(() => {
          navigate('/signin');
        }, 3000);
      }
    } catch (err) {
      setError(err?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!validToken) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
            <div className="flex items-center justify-center mb-6">
              <div className="bg-error/10 rounded-full p-4">
                <Icon name="XCircle" size={48} className="text-error" />
              </div>
            </div>
            
            <h1 className="text-2xl font-bold text-center text-foreground mb-2">
              Invalid Reset Link
            </h1>
            <p className="text-center text-muted-foreground mb-6">
              This password reset link is invalid or has expired. Please request a new one.
            </p>

            <Button
              fullWidth
              onClick={() => navigate('/forgot-password')}
              iconName="Mail"
            >
              Request New Link
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
            <div className="flex items-center justify-center mb-6">
              <div className="bg-success/10 rounded-full p-4">
                <Icon name="CheckCircle" size={48} className="text-success" />
              </div>
            </div>
            
            <h1 className="text-2xl font-bold text-center text-foreground mb-2">
              Password Reset Successful
            </h1>
            <p className="text-center text-muted-foreground mb-6">
              Your password has been successfully reset. Redirecting to sign in...
            </p>

            <Button
              fullWidth
              onClick={() => navigate('/signin')}
              iconName="LogIn"
            >
              Go to Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
          <div className="flex items-center justify-center mb-6">
            <div className="bg-primary rounded-full p-3">
              <Icon name="Key" size={32} className="text-white" />
            </div>
          </div>
          
          <h1 className="text-2xl font-bold text-center text-foreground mb-2">
            Reset Your Password
          </h1>
          <p className="text-center text-muted-foreground mb-6">
            Enter your new password below
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="password"
              label="New Password"
              value={password}
              onChange={(e) => setPassword(e?.target?.value)}
              placeholder="Enter new password"
              required
            />

            <Input
              type="password"
              label="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e?.target?.value)}
              placeholder="Confirm new password"
              required
            />

            <div className="bg-muted/50 rounded-md p-3">
              <p className="text-xs text-muted-foreground">
                <Icon name="Info" size={14} className="inline mr-1" />
                Password must be at least 6 characters long
              </p>
            </div>

            {error && (
              <div className="bg-error/10 border border-error rounded-md p-3 flex items-start gap-2">
                <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
                <p className="text-sm text-error">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              fullWidth
              loading={loading}
              iconName="Lock"
              disabled={loading}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;